// providers/helpers.ts
/**
 * @fileoverview Helper functions for creating provider configurations.
 *
 * These factory functions simplify provider config creation by:
 * - Providing sensible defaults
 * - Reducing boilerplate
 * - Enforcing consistent patterns
 * - Supporting boolean shortcuts (e.g., `customFields: true`)
 *
 * ## Quick Start
 *
 * Use the `provider()` function for the simplest config syntax:
 *
 * @example
 * ```typescript
 * import { provider, providerTag, toggle } from './helpers';
 *
 * export const myConfig = provider(Api.IntegrationProvider.MyProvider, {
 *   displayName: 'My Provider',
 *
 *   // Tags section - simplified syntax
 *   tags: {
 *     coreTags: true,                    // Enable standard location tags
 *     customFields: true,                // Enable custom fields with standard paths
 *     providerTags: [
 *       providerTag('ContactType'),      // Auto-generates settingsKey
 *       providerTag('Status'),
 *     ],
 *   },
 *
 *   // Main settings - simplified syntax
 *   main: {
 *     writebacks: true,
 *     fields: [
 *       toggle('accountTagsEnabled', 'Account Tags'),
 *     ],
 *   },
 *
 *   // Key facts - simplified syntax
 *   keyFacts: {
 *     birthdayDisplay: true,
 *     customDateFields: true,            // Enable with standard paths
 *   },
 * });
 * ```
 */

import * as Api from '@ViewModels';
import { ConfigPath, SettingsKey } from './constants';
import type {
	ComboboxField,
	ContactViewSelectField,
	ComboboxItem,
	ComplementSelectField,
	ConfigurationSchema,
	ConnectionSyncPredicate,
	CustomTagSection,
	DynamicCustomTagSection,
	KeyFactsSchema,
	LockableSelectField,
	MainSettingsSchema,
	NumberField,
	ProviderConfig,
	ProviderTagOption,
	SelectField,
	SettingsField,
	TagsSchema,
	ToggleField,
	VisibleWhenCondition,
} from './types';

// ============================================================================
// String Utilities
// ============================================================================

/**
 * Convert a string to camelCase.
 * 'ContactType' -> 'contactType'
 * 'Do Not Mail' -> 'doNotMail'
 */
function toCamelCase(str: string): string {
	return str
		.replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase())
		.replace(/^[A-Z]/, char => char.toLowerCase());
}

/**
 * Convert a PascalCase or camelCase string to a human-readable label.
 * 'ContactType' -> 'Contact Type'
 * 'doNotMail' -> 'Do Not Mail'
 */
function toLabel(str: string): string {
	return str
		.replace(/([A-Z])/g, ' $1')
		.replace(/^./, char => char.toUpperCase())
		.trim();
}

// ============================================================================
// Provider Tag Helpers
// ============================================================================

/**
 * Create a provider tag option with automatic settingsKey inference.
 *
 * The settingsKey is generated using the convention: `camelCase(id) + 'TagEnabled'`
 *
 * @param id - The tag identifier (e.g., 'ContactType', 'DoNotMail')
 * @param label - Optional custom label. Defaults to humanized version of id.
 * @returns ProviderTagOption with inferred settingsKey
 *
 * @example
 * ```typescript
 * providerTag('ContactType')
 * // => { id: 'ContactType', label: 'Contact Type', settingsKey: 'contactTypeTagEnabled' }
 *
 * providerTag('DoNotMail', 'Do Not Mail')
 * // => { id: 'DoNotMail', label: 'Do Not Mail', settingsKey: 'doNotMailTagEnabled' }
 * ```
 */
export function providerTag(id: string, label?: string): ProviderTagOption {
	return {
		id,
		label: label ?? toLabel(id),
		settingsKey: `${toCamelCase(id)}TagEnabled`,
	};
}

/**
 * Create multiple provider tags from an array of IDs.
 *
 * @param ids - Array of tag identifiers
 * @returns Array of ProviderTagOption with inferred settingsKeys
 *
 * @example
 * ```typescript
 * providerTags(['ContactType', 'Status', 'Active'])
 * // => [
 * //   { id: 'ContactType', label: 'Contact Type', settingsKey: 'contactTypeTagEnabled' },
 * //   { id: 'Status', label: 'Status', settingsKey: 'statusTagEnabled' },
 * //   { id: 'Active', label: 'Active', settingsKey: 'activeTagEnabled' },
 * // ]
 * ```
 */
export function providerTags(ids: string[]): ProviderTagOption[] {
	return ids.map(id => providerTag(id));
}

// ============================================================================
// Field Factory Functions
// ============================================================================

/** Common options for nested field support */
interface NestedFieldOptions {
	/** Parent key for nested settings (e.g., 'prospectOptions') */
	parentKey?: string;
	/** Only show this field when another setting has a specific value */
	visibleWhen?: VisibleWhenCondition;
	/** Whether this is a sub-setting (renders indented) */
	isSubSetting?: boolean;
}

interface ToggleOptions extends NestedFieldOptions {
	/** Display label for the field */
	label?: string;
	/** Helper text shown below the label */
	subtitle?: string;
}

/**
 * Create a toggle field configuration.
 *
 * @param key - The settings key this field maps to
 * @param labelOrOptions - Either a label string or options object
 * @returns ToggleField configuration
 *
 * @example
 * ```typescript
 * toggle('writebacksEnabled')
 * // => { type: 'toggle', key: 'writebacksEnabled', label: 'Writebacks Enabled' }
 *
 * toggle('syncEnabled', 'Enable Sync')
 * // => { type: 'toggle', key: 'syncEnabled', label: 'Enable Sync' }
 *
 * toggle('accountTags', { label: 'Account Tags', subtitle: 'Tag contacts by account' })
 * ```
 */
export function toggle(key: string, labelOrOptions?: string | ToggleOptions): ToggleField {
	const options = typeof labelOrOptions === 'string' ? { label: labelOrOptions } : (labelOrOptions ?? {});
	return {
		type: 'toggle',
		key,
		label: options.label ?? toLabel(key),
		subtitle: options.subtitle,
		parentKey: options.parentKey,
		visibleWhen: options.visibleWhen,
		isSubSetting: options.isSubSetting,
	};
}

interface SelectOptions extends NestedFieldOptions {
	/** Display label for the field */
	label?: string;
	/** Helper text shown below the label */
	subtitle?: string;
	/** Placeholder text when no value selected */
	placeholder?: string;
	/** Whether the field is disabled */
	disabled?: boolean;
}

/**
 * Create a select (dropdown) field configuration.
 *
 * @param key - The settings key this field maps to
 * @param optionsFrom - Path to options in configuration, or static options array
 * @param options - Additional field options
 * @returns SelectField configuration
 *
 * @example
 * ```typescript
 * // With configuration path
 * select('recordType', ConfigPath.AVAILABLE_RECORD_TYPES)
 *
 * // With static options
 * select('leadAttorneyTag', [
 *   { id: 'None', label: 'None' },
 *   { id: 'MostRecent', label: 'Most Recent' },
 * ], { label: 'Lead Attorney Tag Options' })
 * ```
 */
export function select(key: string, optionsFrom: string | ComboboxItem[], options?: SelectOptions): SelectField {
	const isStatic = Array.isArray(optionsFrom);
	return {
		type: 'select',
		key,
		optionsFrom: isStatic ? undefined : optionsFrom,
		staticOptions: isStatic ? optionsFrom : undefined,
		label: options?.label ?? toLabel(key),
		subtitle: options?.subtitle,
		placeholder: options?.placeholder,
		disabled: options?.disabled,
		parentKey: options?.parentKey,
		visibleWhen: options?.visibleWhen,
		isSubSetting: options?.isSubSetting,
	};
}

interface LockableSelectOptions extends SelectOptions {
	/** The "unset" value that allows editing (default: 'Unknown') */
	initialValue?: string;
	/** Error message shown when value equals initialValue */
	initialValueError?: string;
	/** Whether to include initialValue as a selectable option when editable */
	showInitialValueOption?: boolean;
}

/**
 * Create a lockable select field - can only be edited when value equals initialValue.
 * Once a value is set (not equal to initialValue), the field becomes read-only.
 *
 * @param key - The settings key this field maps to
 * @param optionsFrom - Path to options in configuration
 * @param options - Additional field options
 * @returns LockableSelectField configuration
 *
 * @example
 * ```typescript
 * lockableSelect('contactRecordType', ConfigPath.AVAILABLE_RECORD_TYPES, {
 *   label: 'Contact Record Type',
 *   subtitle: 'Cannot be changed once set',
 * })
 * ```
 */
export function lockableSelect(key: string, optionsFrom: string, options?: LockableSelectOptions): LockableSelectField {
	return {
		type: 'lockableSelect',
		key,
		optionsFrom,
		label: options?.label ?? toLabel(key),
		subtitle: options?.subtitle,
		placeholder: options?.placeholder,
		initialValue: options?.initialValue ?? 'Unknown',
		initialValueError: options?.initialValueError ?? `(No ${toLabel(key).toLowerCase()} selected)`,
		showInitialValueOption: options?.showInitialValueOption ?? true,
	};
}

interface SearchableSelectOptions {
	/** Display label for the field */
	label?: string;
	/** Helper text shown below the label */
	subtitle?: string;
	/** Placeholder for the search input */
	searchPlaceholder?: string;
	/** Whether multiple values can be selected */
	multiple?: boolean;
	/** Hide the row entirely when the resolved options list is empty */
	hideWhenNoOptions?: boolean;
}

/**
 * Create a searchable select (combobox) field configuration.
 * Use this for large option lists where search/filter is helpful.
 *
 * @param key - The settings key this field maps to
 * @param optionsFrom - Path to options in configuration (use ConfigPath.ALL_AVAILABLE_FIELDS for combined)
 * @param options - Additional field options
 * @returns ComboboxField configuration
 *
 * @example
 * ```typescript
 * // Single-select searchable field
 * searchableSelect('deceasedField', ConfigPath.ALL_AVAILABLE_FIELDS, {
 *   label: 'Deceased Indicator',
 * })
 *
 * // Multi-select searchable field
 * searchableSelect('unsubscribeFields', ConfigPath.ALL_AVAILABLE_FIELDS, {
 *   label: 'Unsubscribe Indicators',
 *   multiple: true,
 * })
 * ```
 */
export function searchableSelect(key: string, optionsFrom: string, options?: SearchableSelectOptions): ComboboxField {
	const label = options?.label ?? toLabel(key);
	return {
		type: 'combobox',
		key,
		optionsFrom,
		label,
		subtitle: options?.subtitle,
		multiple: options?.multiple ?? false,
		searchPlaceholder: options?.searchPlaceholder ?? `Search ${label.toLowerCase()}...`,
		hideWhenNoOptions: options?.hideWhenNoOptions,
	};
}

export function contactViewSelect(options?: {
	label?: string;
	subtitle?: string;
	searchPlaceholder?: string;
}): ContactViewSelectField {
	const label = options?.label ?? 'Contact Views';
	return {
		type: 'contactViewSelect',
		key: 'contactViews',
		label,
		subtitle: options?.subtitle,
		searchPlaceholder: options?.searchPlaceholder ?? `Search ${label.toLowerCase()}...`,
	};
}

interface NumberOptions extends NestedFieldOptions {
	/** Display label for the field */
	label?: string;
	/** Helper text shown below the label */
	subtitle?: string;
	min?: number;
	max?: number;
	placeholder?: string;
	/** Suffix for display value (e.g., 'year' produces '1 year' / '3 years') */
	displaySuffix?: string;
}

/**
 * Create a number input field configuration.
 *
 * @param key - The settings key this field maps to
 * @param options - Field options
 * @returns NumberField configuration
 *
 * @example
 * ```typescript
 * number('timelineYears', {
 *   label: 'Timeline Years',
 *   min: 1, max: 10,
 *   displaySuffix: 'year',
 *   parentKey: 'prospectOptions',
 * })
 * ```
 */
export function number(key: string, options?: NumberOptions): NumberField {
	return {
		type: 'number',
		key,
		label: options?.label ?? toLabel(key),
		subtitle: options?.subtitle,
		min: options?.min,
		max: options?.max,
		placeholder: options?.placeholder,
		displaySuffix: options?.displaySuffix,
		parentKey: options?.parentKey,
		visibleWhen: options?.visibleWhen,
		isSubSetting: options?.isSubSetting,
	};
}

interface ComplementSelectOptions {
	/** Display label for the field */
	label?: string;
	/** Helper text shown below the label */
	subtitle?: string;
	/** Placeholder for the search input */
	searchPlaceholder?: string;
}

/**
 * Create a complement multi-select field configuration.
 *
 * The user selects a subset of the options sourced from a configuration list; the *unselected*
 * options (the complement) are persisted to `key`. The selector is hidden when its option list is
 * empty. Use whenever the stored value should be the inverse of the user's selection.
 *
 * @param key - The settings key that stores the complement (unselected values), e.g. 'blockedStatuses'
 * @param optionsFrom - Configuration path to the full set of options (e.g. 'availableStatuses')
 * @param options - Additional field options
 * @returns ComplementSelectField configuration
 *
 * @example
 * ```typescript
 * complementSelect('blockedStatuses', 'availableStatuses', {
 *   label: 'Allowed Statuses',
 *   subtitle: 'Select which statuses load into Levitate',
 * })
 * ```
 */
export function complementSelect(
	key: string,
	optionsFrom: string,
	options?: ComplementSelectOptions
): ComplementSelectField {
	const label = options?.label ?? toLabel(key);
	return {
		type: 'complementSelect',
		key,
		optionsFrom,
		label,
		subtitle: options?.subtitle,
		searchPlaceholder: options?.searchPlaceholder ?? `Search ${label.toLowerCase()}...`,
	};
}

// ============================================================================
// Custom Section Helpers
// ============================================================================

interface DynamicSectionOptions {
	/** Display label for the section */
	label?: string;
	/** Helper text shown below the label */
	subtitle?: string;
	/** Nested settings key (e.g., 'interestTagSettings') */
	settingsKey: string;
	/** Key within nested settings for the IDs array (e.g., 'excludedTags') */
	enabledKey: string;
	/** Field mapping: property name for item ID */
	idKey: string;
	/** Field mapping: property name for item label */
	labelKey: string;
	/** Field mapping: property name for item description (joined if array) */
	descriptionKey?: string;
	/** When true, renders a master toggle + "Manage" button that opens a modal */
	enableToggle?: boolean;
	/** Key within nested settings for the enabled boolean (default: 'enabled') */
	enabledToggleKey?: string;
	/** Label for the manage button (default: 'Manage') */
	manageButtonLabel?: string;
}

/**
 * Create a dynamic custom tag section with options loaded from configuration.
 * Uses field mapping keys so TagSettingsGroup stays provider-agnostic.
 *
 * @param id - Unique section identifier
 * @param optionsFrom - Configuration path to the available options array
 * @param options - Section options including field mapping and toggle config
 * @returns DynamicCustomTagSection configuration
 *
 * @example
 * ```typescript
 * // Simple inclusion model (user picks which to enable)
 * dynamicSection('myTags', ConfigPath.AVAILABLE_MY_TAGS, {
 *   settingsKey: 'myTagSettings',
 *   enabledKey: 'enabledTags',
 *   idKey: 'tagId',
 *   labelKey: 'tagName',
 * })
 *
 * // Toggle + exclusion model (all active by default, user excludes some)
 * dynamicSection('interestTags', ConfigPath.AVAILABLE_INTEREST_TAGS, {
 *   label: 'Interest Tags',
 *   subtitle: 'Tag contacts based on interest tag assignments',
 *   settingsKey: 'interestTagSettings',
 *   enabledKey: 'excludedTags',
 *   idKey: 'tagId',
 *   labelKey: 'tagName',
 *   descriptionKey: 'categoryNames',
 *   enableToggle: true,
 *   manageButtonLabel: 'Manage Tags',
 * })
 * ```
 */
export function dynamicSection(
	id: string,
	optionsFrom: string,
	options: DynamicSectionOptions
): DynamicCustomTagSection {
	return {
		id,
		label: options.label ?? toLabel(id),
		subtitle: options.subtitle ?? '',
		storageType: 'dynamic',
		optionsFrom,
		settingsKey: options.settingsKey,
		enabledKey: options.enabledKey,
		idKey: options.idKey,
		labelKey: options.labelKey,
		descriptionKey: options.descriptionKey,
		enableToggle: options.enableToggle,
		enabledToggleKey: options.enabledToggleKey,
		manageButtonLabel: options.manageButtonLabel,
	};
}

// ============================================================================
// Simplified Provider Configuration
// ============================================================================

/**
 * Simplified tags configuration input.
 * Supports boolean shortcuts for common patterns.
 */
export interface SimpleTagsInput {
	/** Enable core location tags (State, City, PostalCode, Country). Default: true */
	coreTags?: boolean;
	/**
	 * Provider-specific tag options.
	 * Use `providerTag()` or `providerTags()` helpers for easy creation.
	 */
	providerTags?: ProviderTagOption[];
	/**
	 * Dynamic tags loaded from configuration.
	 * - `true`: Use standard paths (availableFields -> enabledFields)
	 * - `string`: Custom optionsFrom path (infers settingsKey as 'enabled' + path suffix)
	 * - `object`: Full configuration
	 */
	dynamicTags?: boolean | string | { optionsFrom: string; settingsKey: string };
	/**
	 * Custom fields for tags.
	 * - `true`: Use standard paths (availableCustomFields -> enabledCustomFields)
	 * - `object`: Custom availableFrom/settingsKey
	 */
	customFields?: boolean | { availableFrom: string; settingsKey: string };
	/** Custom tag sections (e.g., Campaign Tags, Interest Tags) */
	customSections?: CustomTagSection[];
	/** Extra fields (toggles, selects, etc.) in the tags section */
	extraFields?: SettingsField[];
}

/**
 * Simplified key facts configuration input.
 */
export interface SimpleKeyFactsInput {
	/** Show birthday display selector. Default: true */
	birthdayDisplay?: boolean;
	/**
	 * Date fields configuration.
	 * - `true`: Use standard paths (availableDateFields -> enabledDateFields)
	 * - `object`: Custom configuration with label/subtitle
	 */
	dateFields?:
		| boolean
		| {
				availableFrom?: string;
				settingsKey?: string;
				label?: string;
				subtitle?: string;
		  };
	/**
	 * Custom date fields configuration.
	 * - `true`: Use standard paths (availableCustomDateFields -> enabledCustomDateFields)
	 * - `object`: Custom configuration with label/subtitle
	 */
	customDateFields?:
		| boolean
		| {
				availableFrom?: string;
				settingsKey?: string;
				label?: string;
				subtitle?: string;
		  };
	/** Extra fields (toggles, selects, etc.) rendered in the Key Facts section */
	extraFields?: SettingsField[];
}

/** Defaults are filled in by {@link normalizeMain}. See {@link WritebackGridSchema}. */
export interface WritebackGridInput {
	optionsFrom: string;
	settingsKey: string;
	placeholder?: string;
	emptyOptionsLabel?: string;
	missingCodeHint?: string;
}

/**
 * Simplified main settings configuration input.
 */
export interface SimpleMainInput {
	/** Default: true. Pass a grid to swap the multi-select for a per-type grid. */
	writebacks?: boolean | { grid: WritebackGridInput };
	/** Extra fields for main settings section */
	fields?: SettingsField[];
}

/**
 * Simplified provider configuration input.
 * Use with the `provider()` function for the cleanest syntax.
 */
export interface SimpleProviderInput {
	/** Human-readable display name (e.g., 'Salesforce', 'ServiceTitan') */
	displayName: string;
	/** Configuration section options */
	configuration?: ConfigurationSchema;
	/** Main settings section */
	main?: SimpleMainInput;
	/** Key facts section */
	keyFacts?: SimpleKeyFactsInput;
	/** Tags section */
	tags?: SimpleTagsInput;
	/** Whether to show key facts section. Default: true if keyFacts defined */
	showKeyFacts?: boolean;
	/** Whether to show the Connections tab. Default: true */
	showConnections?: boolean;
	/**
	 * Override deciding whether a user connection is sync-enabled in the Connections view.
	 * Defaults to a `configuration.syncUserId` match when omitted.
	 */
	isConnectionSyncEnabled?: ConnectionSyncPredicate;
}

// ============================================================================
// Normalization Functions
// ============================================================================

/**
 * Normalize simplified tags input to full TagsSchema.
 */
function normalizeTags(input?: SimpleTagsInput): TagsSchema | undefined {
	if (!input) return undefined;

	const result: TagsSchema = {
		showCoreTags: input.coreTags ?? true,
		providerTags: input.providerTags,
		extraFields: input.extraFields,
		customSections: input.customSections,
	};

	// Handle dynamicTags shortcut
	if (input.dynamicTags === true) {
		result.dynamicTags = {
			optionsFrom: ConfigPath.AVAILABLE_FIELDS,
			settingsKey: SettingsKey.ENABLED_FIELDS,
		};
	} else if (typeof input.dynamicTags === 'string') {
		// Infer settingsKey from optionsFrom: 'availableXxx' -> 'enabledXxx'
		const settingsKey = input.dynamicTags.replace(/^available/, 'enabled');
		result.dynamicTags = {
			optionsFrom: input.dynamicTags,
			settingsKey,
		};
	} else if (input.dynamicTags && typeof input.dynamicTags === 'object') {
		result.dynamicTags = input.dynamicTags;
	}

	// Handle customFields shortcut
	if (input.customFields === true) {
		result.customFields = {
			availableFrom: ConfigPath.AVAILABLE_CUSTOM_FIELDS,
			settingsKey: SettingsKey.ENABLED_CUSTOM_FIELDS,
		};
	} else if (input.customFields && typeof input.customFields === 'object') {
		result.customFields = input.customFields;
	}

	return result;
}

/**
 * Normalize simplified key facts input to full KeyFactsSchema.
 */
function normalizeKeyFacts(input?: SimpleKeyFactsInput): KeyFactsSchema | undefined {
	if (!input) return undefined;

	const result: KeyFactsSchema = {
		showBirthdayDisplay: input.birthdayDisplay ?? true,
		extraFields: input.extraFields,
	};

	// Handle dateFields shortcut
	if (input.dateFields === true) {
		result.dateFields = {
			availableFrom: ConfigPath.AVAILABLE_DATE_FIELDS,
			settingsKey: SettingsKey.ENABLED_DATE_FIELDS,
			label: 'Date Fields',
			subtitle: 'Select date fields to be used as key facts',
		};
	} else if (input.dateFields && typeof input.dateFields === 'object') {
		result.dateFields = {
			availableFrom: input.dateFields.availableFrom ?? ConfigPath.AVAILABLE_DATE_FIELDS,
			settingsKey: input.dateFields.settingsKey ?? SettingsKey.ENABLED_DATE_FIELDS,
			label: input.dateFields.label ?? 'Date Fields',
			subtitle: input.dateFields.subtitle ?? 'Select date fields to be used as key facts',
		};
	}

	// Handle customDateFields shortcut
	if (input.customDateFields === true) {
		result.customDateFields = {
			availableFrom: ConfigPath.AVAILABLE_CUSTOM_DATE_FIELDS,
			settingsKey: SettingsKey.ENABLED_CUSTOM_DATE_FIELDS,
			label: 'Custom Date Fields',
			subtitle: 'Select custom date fields to be used as key facts',
		};
	} else if (input.customDateFields && typeof input.customDateFields === 'object') {
		result.customDateFields = {
			availableFrom: input.customDateFields.availableFrom ?? ConfigPath.AVAILABLE_CUSTOM_DATE_FIELDS,
			settingsKey: input.customDateFields.settingsKey ?? SettingsKey.ENABLED_CUSTOM_DATE_FIELDS,
			label: input.customDateFields.label ?? 'Custom Date Fields',
			subtitle: input.customDateFields.subtitle ?? 'Select custom date fields to be used as key facts',
		};
	}

	return result;
}

/**
 * Normalize simplified main input to full MainSettingsSchema.
 */
function normalizeMain(input?: SimpleMainInput): MainSettingsSchema | undefined {
	if (!input) return undefined;

	const { writebacks } = input;
	const grid = typeof writebacks === 'object' ? writebacks.grid : undefined;

	return {
		showWritebacks: writebacks !== false,
		writebackGrid: grid
			? {
					optionsFrom: grid.optionsFrom,
					settingsKey: grid.settingsKey,
					placeholder: grid.placeholder ?? 'Choose a code',
					emptyOptionsLabel: grid.emptyOptionsLabel ?? 'No options available',
					missingCodeHint: grid.missingCodeHint,
				}
			: undefined,
		extraFields: input.fields,
	};
}

// ============================================================================
// Provider Factory Function
// ============================================================================

/**
 * Create a provider configuration with simplified syntax.
 *
 * This is the recommended way to create provider configs. It provides:
 * - Sensible defaults for all sections
 * - Boolean shortcuts for common patterns
 * - Type inference from provider enum
 * - Cleaner, more readable configs
 *
 * @param providerEnum - The IntegrationProvider enum value
 * @param input - Simplified configuration input
 * @returns Full ProviderConfig ready for use
 *
 * @example
 * ```typescript
 * // Simple provider with standard features
 * export const myConfig = provider(Api.IntegrationProvider.MyProvider, {
 *   displayName: 'My Provider',
 *   tags: {
 *     coreTags: true,
 *     customFields: true,
 *     providerTags: [
 *       providerTag('ContactType'),
 *       providerTag('Status'),
 *     ],
 *   },
 *   keyFacts: {
 *     birthdayDisplay: true,
 *   },
 * });
 *
 * // Complex provider with custom configuration
 * export const salesforceConfig = provider(Api.IntegrationProvider.Salesforce, {
 *   displayName: 'Salesforce',
 *   tags: {
 *     coreTags: false,
 *     dynamicTags: true,  // Uses availableFields -> enabledFields
 *     customFields: true,
 *   },
 *   main: {
 *     writebacks: true,
 *     fields: [
 *       lockableSelect('contactRecordType', ConfigPath.AVAILABLE_RECORD_TYPES),
 *     ],
 *   },
 * });
 * ```
 */
export function provider(providerEnum: Api.IntegrationProvider, input: SimpleProviderInput): ProviderConfig {
	const tags = normalizeTags(input.tags);
	const keyFacts = normalizeKeyFacts(input.keyFacts);
	const main = normalizeMain(input.main);

	return {
		provider: providerEnum,
		displayName: input.displayName,
		configuration: input.configuration,
		main,
		keyFacts,
		tags,
		showKeyFacts: input.showKeyFacts ?? keyFacts !== undefined,
		showConnections: input.showConnections ?? true,
		isConnectionSyncEnabled: input.isConnectionSyncEnabled,
	};
}
